import { Link } from "@tanstack/react-router";
import { Clock, Flame, Heart, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatUsd, servicePath, type MpServiceCard } from "@/lib/marketplace/queries";
import { useT } from "@/i18n";

export function MarketplaceServiceCard({ service, promoted }: { service: MpServiceCard; promoted?: boolean }) {
  const { t } = useT();
  const link = servicePath(service.seller, service.slug, service.seller_user_id);
  return (
    <Link
      to={link.to}
      params={link.params}
      className="group card-surface flex flex-col overflow-hidden transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] bg-muted">
        {service.cover_url ? (
          <img
            src={service.cover_url}
            alt={service.title}
            loading="lazy"
            className="size-full object-cover transition-transform group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5 font-display text-3xl font-bold text-primary/40">
            {service.title.slice(0, 1).toUpperCase()}
          </div>
        )}
        {promoted && (
          <span className="absolute left-2 top-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            {t("mp.card.promoted")}
          </span>
        )}
        {service.is_ambassador_pick && (
          <Badge className="absolute right-2 top-2 bg-primary text-primary-foreground">{t("mp.card.ambassador_pick")}</Badge>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <div className="size-6 overflow-hidden rounded-full bg-muted">
            {service.seller?.avatar_url && (
              <img src={service.seller.avatar_url} alt="" className="size-full object-cover" loading="lazy" />
            )}
          </div>
          <span className="truncate text-xs font-medium text-muted-foreground">
            {service.seller?.display_name || service.seller?.handle || t("mp.card.seller_fallback")}
          </span>
        </div>
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug">{service.title}</h3>
        <div className="mt-auto flex items-center gap-3 text-xs text-muted-foreground">
          {service.rating_count > 0 && (
            <span className="flex items-center gap-1">
              <Star className="size-3.5 fill-amber-400 text-amber-400" />
              {Number(service.rating_avg).toFixed(1)} ({service.rating_count})
            </span>
          )}
          {service.hot_score > 0 && (
            <span className="flex items-center gap-1 text-primary">
              <Flame className="size-3.5" />
              {Math.round(service.hot_score)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="size-3.5" />
            {service.delivery_days}d
          </span>
          <span className="flex items-center gap-1">
            <Heart className="size-3.5" />
            {service.favorites_count}
          </span>
        </div>
        <div className="border-t border-border pt-2 text-sm">
          <span className="text-xs text-muted-foreground">{t("mp.card.from")}</span>
          <span className="font-display font-bold">{formatUsd(service.starting_price_cents)}</span>
        </div>
      </div>
    </Link>
  );
}
